import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import {
  BatteryChargingFull,
  Bolt,
  DeviceHub,
  ElectricalServices,
  Light,
  Lightbulb,
  MyLocation,
  Nfc,
  Sensors,
  Straighten,
  Thermostat,
  Tv,
  VolumeUp,
} from '@mui/icons-material';
import { deviceApi, fileStoreApi, flowApi } from '../api';
import { SchemaObjectForm } from '../components/SchemaForm';
import StripRefsField from './StripRefsField';
import ChannelRefsField from './ChannelRefsField';
import {
  ConfigSchema,
  ConfigValues,
  defaultsFromFields,
  deviceItemTypes,
  generateConfigJson,
  mergeConfig,
} from '../schema/schema';
import {
  GraphDevice,
  GraphRoute,
  MAX_DEVICES,
  ensureGraph,
  mergeRoutes,
  newDeviceId,
  readDevices,
  readRoutes,
  stacklightStripsReady,
  stacklightChannelsReady,
  stacklightUsesWs2812,
} from '../flow/graph';
import {
  mergePeripheryIntoDoc,
  suggestedFlowLabel,
  suggestedFlowName,
  uniqueFlowName,
  widgetsForDeviceType,
  PeripheryWidgetType,
} from '../flow/peripheryBundle';
import { defaultFlowFile, parseFlowFile, serializeFlowFile } from '../flow/flowFile';
import { catalogWizardFor } from '../flow/catalogWizard';
import { defaultCatalogFlow, parseCatalogFlow, serializeCatalogFlow } from '../flow/catalogDoc';
import { slugCatalogName } from '../files/catalogPaths';
import { useI18n } from '../i18n';
import { FleetEntry, controllerFamily, filterFleet, fleetLabel, loadFleet } from '../controllers/fleet';
import { rememberControllerId } from '../nav/paths';
import { LayoutInfo } from '../types';
import SignalsIcon from '../icons/SignalsIcon';

const SETTINGS_PATH = '/settings.json';

const TILE_ORDER = [
  'stacklight_legacy', 'stacklight', 'sht21', 'sml', 'gps', 'pn532', 'vl53l0x', 'ws2812',
  'gpio_led', 'gpio_buzzer', 'gpio_relay', 'gpio_input', 'modbus_slave', 'rgb_tft', 'victron',
];

const TYPE_ICONS: Record<string, typeof DeviceHub> = {
  sht21: Thermostat,
  sml: Bolt,
  gps: MyLocation,
  pn532: Nfc,
  vl53l0x: Straighten,
  ws2812: Light,
  gpio_led: Lightbulb,
  gpio_buzzer: VolumeUp,
  gpio_relay: ElectricalServices,
  gpio_input: Sensors,
  stacklight_legacy: ElectricalServices,
  modbus_slave: DeviceHub,
  rgb_tft: Tv,
  victron: BatteryChargingFull,
};

type PeripheryDialogProps = {
  open: boolean;
  onClose: () => void;
  mode: 'local' | 'persist';
  controllerId?: string;
  controllerHostname?: string;
  existingDevices?: GraphDevice[];
  currentFlowName?: string;
  currentFlowContent?: string;
  onAdd?: (device: GraphDevice, extras?: { routes?: GraphRoute[] }) => void;
  onPersisted?: (controllerId: string, device: GraphDevice) => void;
  onFlowCommitted?: (info: { name: string; created: boolean; doc?: ReturnType<typeof parseFlowFile> }) => void;
};

function widgetLabel(
  type: PeripheryWidgetType,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  if (type === 'none') return t('deviceConfig.wizard.widgetNone');
  if (type === 'temperature') return t('flow.climateWidget');
  if (type === 'stacklight') return t('flow.stacklightWidget');
  if (type === 'switch') return t('deviceConfig.wizard.iconSwitch');
  if (type === 'bulb') return t('deviceConfig.wizard.iconBulb');
  if (type === 'relay') return t('flow.relayWidget');
  if (type === 'gps_map') return t('flow.types.gps');
  if (type === 'sml_meter') return t('flow.types.sml');
  if (type === 'nfc_tag') return t('flow.types.pn532');
  if (type === 'distance') return t('dashboard.distance');
  return type;
}

function TypeIcon({ type, size = 56 }: { type: string; size?: number }): React.ReactElement {
  if (type === 'stacklight' || type === 'stacklight_legacy') {
    return <SignalsIcon sx={{ fontSize: size }} />;
  }
  const Icon = TYPE_ICONS[type] || DeviceHub;
  return <Icon sx={{ fontSize: size }} />;
}

function orderedTypes(types: string[]): string[] {
  const rest = types.filter((item) => !TILE_ORDER.includes(item));
  return TILE_ORDER.filter((item) => types.includes(item) || item === 'stacklight_legacy').concat(
    rest.filter((item) => item !== 'stacklight_legacy'),
  );
}

/** Require at least one configured GPIO before leaving the settings step. */
function peripheryGpiosReady(type: string, values: ConfigValues): boolean {
  if (type === 'gpio_input') {
    const lines = Array.isArray(values.lines) ? values.lines : [];
    return lines.some((row) => {
      if (!row || typeof row !== 'object') {
        return false;
      }
      const line = row as Record<string, unknown>;
      if (line.enable === false) {
        return false;
      }
      const pin = Number(line.pin);
      return Number.isFinite(pin) && pin >= 0 && String(line.key || '').trim() !== '';
    });
  }
  if (type === 'gpio_relay') {
    const pins = Array.isArray(values.pins)
      ? (values.pins as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n) && n >= 0)
      : [];
    return pins.length > 0;
  }
  if (type === 'gpio_led' || type === 'gpio_buzzer') {
    const pin = Number(values.pin);
    return Number.isFinite(pin) && pin >= 0;
  }
  return true;
}

const PeripheryDialog: React.FC<PeripheryDialogProps> = ({
  open,
  onClose,
  mode,
  controllerId,
  controllerHostname,
  existingDevices = [],
  currentFlowName,
  currentFlowContent,
  onAdd,
  onPersisted,
  onFlowCommitted,
}) => {
  const { t } = useI18n();
  const [schema, setSchema] = useState<ConfigSchema | null>(null);
  const [fleet, setFleet] = useState<FleetEntry[]>([]);
  const [selectedController, setSelectedController] = useState<FleetEntry | null>(null);
  const [type, setType] = useState('');
  const [id, setId] = useState('');
  const [values, setValues] = useState<ConfigValues>({});
  const [remoteDevices, setRemoteDevices] = useState<GraphDevice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [flowMode, setFlowMode] = useState<'none' | 'new' | 'existing'>('new');
  const [flows, setFlows] = useState<LayoutInfo[]>([]);
  const [existingFlow, setExistingFlow] = useState('');
  const [newFlowName, setNewFlowName] = useState('');
  const [flowNameTouched, setFlowNameTouched] = useState(false);
  const [widget, setWidget] = useState<PeripheryWidgetType>('none');

  const itemTypes = schema ? deviceItemTypes(schema) : {};
  const peripheryTypes = orderedTypes(
    Object.keys(itemTypes).filter((item) => itemTypes[item].layer !== 'connection'),
  );
  const spec = type && type !== 'stacklight_legacy' ? itemTypes[type] : undefined;
  const formFields = useMemo(() => {
    const fields = { ...(spec?.fields || {}) };
    delete fields.id;
    delete fields.type;
    delete fields.strips;
    delete fields.channels;
    // Channel count follows pins[] — do not show a separate count field.
    delete fields.count;
    return fields;
  }, [spec]);

  const targetId = mode === 'local' ? (controllerId || '') : (selectedController?.id || '');
  const hostname = (mode === 'persist' ? selectedController?.hostname : controllerHostname)
    || selectedController?.hostname
    || controllerHostname
    || targetId;
  const idSource = mode === 'local' ? existingDevices : remoteDevices;
  const widgetChoices = widgetsForDeviceType(type);
  const suggestedName = uniqueFlowName(
    suggestedFlowName(hostname, id.trim() || type),
    flows.map((item) => item.name),
  );
  const flowName = flowMode === 'new'
    ? slugCatalogName(newFlowName || suggestedName)
    : slugCatalogName(existingFlow || currentFlowName || '');

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    setError(null);
    setBusy(false);
    setLoading(true);
    setStep(0);
    setType('');
    setFlowMode('new');
    setFlowNameTouched(false);
    setNewFlowName('');
    (async () => {
      try {
        const [nextSchema, flowList] = await Promise.all([fileStoreApi.getSchema(), flowApi.list().catch(() => [] as LayoutInfo[])]);
        if (cancelled) {
          return;
        }
        setSchema(nextSchema);
        setFlows(flowList);
        setWidget('none');
        setRemoteDevices([]);
        const nextFleet = await loadFleet().catch(() => [] as FleetEntry[]);
        if (cancelled) {
          return;
        }
        setFleet(nextFleet);
        if (mode === 'persist') {
          const initial = nextFleet.find((item) => item.id === controllerId) || null;
          setSelectedController(initial);
        } else {
          setSelectedController(null);
        }
        const current = currentFlowName && flowList.some((item) => item.name === currentFlowName)
          ? currentFlowName
          : (flowList[0]?.name || '');
        setExistingFlow(current);
        if (current) {
          setFlowMode(currentFlowName ? 'existing' : 'new');
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || t('deviceConfig.loadFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode, controllerId, currentFlowName, t]);

  useEffect(() => {
    if (!open || flowNameTouched || !type) {
      return;
    }
    setNewFlowName(suggestedName);
  }, [open, type, hostname, id, flows, suggestedName, flowNameTouched]);

  useEffect(() => {
    if (!open || mode !== 'persist' || !targetId || !schema) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const stored = await deviceApi.getStoredFile(targetId, SETTINGS_PATH);
        if (cancelled) {
          return;
        }
        const merged = mergeConfig(schema, stored?.content || '');
        setRemoteDevices(readDevices(ensureGraph(merged as Record<string, unknown>)));
      } catch {
        if (!cancelled) {
          setRemoteDevices([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode, targetId, schema]);

  const idKey = idSource.map((item) => item.id).join(',');
  useEffect(() => {
    if (!open || !type) {
      return;
    }
    const nextSpec = type === 'stacklight_legacy' ? undefined : itemTypes[type];
    const nextId = type === 'stacklight_legacy' ? 'stacklight' : newDeviceId(type, idSource);
    setId(nextId);
    setValues({ ...defaultsFromFields(nextSpec?.fields), id: nextId, type, enable: true });
    setWidget(widgetsForDeviceType(type)[0] || 'none');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, schema, idKey]);

  const buildDevice = (): GraphDevice | null => {
    const trimmed = id.trim();
    if (!type || !trimmed) {
      return null;
    }
    if (type === 'stacklight_legacy') {
      return { id: trimmed || 'inputs', type: 'stacklight_legacy', enable: true } as GraphDevice;
    }
    const next = { ...values, id: trimmed, type, enable: values.enable !== false } as GraphDevice;
    if (type === 'gpio_relay') {
      const pins = Array.isArray(next.pins)
        ? (next.pins as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
        : [];
      next.pins = pins.slice(0, 8);
      next.count = pins.length;
    }
    return next;
  };

  const persistFlow = async (device: GraphDevice, zigbeeLink = false) => {
    if (flowMode === 'none') {
      return { name: '', created: false, catalog: undefined, doc: undefined };
    }
    const name = flowName;
    if (!name) {
      throw new Error(t('deviceConfig.wizard.flowPick'));
    }
    const created = flowMode === 'new';
    const wizard = catalogWizardFor(device.type);
    if (wizard) {
      let raw: unknown = null;
      if (!created) {
        if (currentFlowContent && currentFlowName && name === slugCatalogName(currentFlowName)) {
          try {
            raw = JSON.parse(currentFlowContent);
          } catch {
            raw = currentFlowContent;
          }
        } else {
          raw = await flowApi.get(name);
        }
      }
      const catalog = wizard.merge(
        created || raw == null ? defaultCatalogFlow(name) : parseCatalogFlow(raw, name).doc,
        {
          flowName: name,
          hostname,
          controllerId: targetId,
          device,
          widget: widget === 'bulb' ? 'switch' : widget,
          icon: widget === 'bulb' ? 'bulb' : (widget === 'switch' ? 'switch' : undefined),
          wireZigbeeUplink: zigbeeLink && (
            device.type === 'stacklight'
            || device.type === 'stacklight_legacy'
            || device.type === 'gpio_relay'
          ),
        },
      );
      await flowApi.save(name, JSON.parse(serializeCatalogFlow(catalog)));
      onFlowCommitted?.({ name, created });
      return { name, created, catalog, doc: undefined };
    }
    let doc;
    if (created) {
      doc = defaultFlowFile(name);
      doc.id = name;
    } else if (currentFlowContent && currentFlowName && name === slugCatalogName(currentFlowName)) {
      doc = parseFlowFile(currentFlowContent, name);
    } else {
      doc = parseFlowFile(JSON.stringify(await flowApi.get(name)), name);
    }
    doc = mergePeripheryIntoDoc(doc, {
      flowMode: flowMode === 'existing' ? 'existing' : 'new',
      flowName: name,
      hostname,
      controllerId: targetId,
      device,
      widget: widget === 'bulb' ? 'switch' : widget,
    });
    await flowApi.save(name, JSON.parse(serializeFlowFile(doc)));
    onFlowCommitted?.({ name, created, doc });
    return { name, created, doc };
  };

  const handleCreate = async () => {
    const device = buildDevice();
    if (!device) {
      return;
    }
    if (idSource.some((item) => item.id === device.id)
      || (type === 'stacklight_legacy' && idSource.some((item) => (
        item.id === 'stacklight' || item.id === 'inputs' || item.type === 'stacklight'
      )))) {
      setError(t('deviceConfig.duplicateId', { id: device.id }));
      return;
    }
    if (!stacklightStripsReady(device, idSource) || !stacklightChannelsReady(device, idSource)) {
      setError(t(stacklightUsesWs2812(device) ? 'deviceConfig.needStrips' : 'deviceConfig.needChannels'));
      return;
    }
    if (mode === 'local') {
      if (idSource.length + 1 > MAX_DEVICES) {
        setError(t('flow.deviceLimit', { max: MAX_DEVICES }));
        return;
      }
      try {
        setBusy(true);
        setError(null);
        if (flowMode === 'none') {
          onAdd?.(device);
        } else {
          await persistFlow(device).then((result) => {
            onAdd?.(device, result.doc ? { routes: result.doc.routes } : undefined);
          });
        }
        onClose();
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || t('deviceConfig.saveFailed'));
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!targetId) {
      setError(t('deviceConfig.controllerRequired'));
      return;
    }
    try {
      setBusy(true);
      setError(null);
      if (!schema) {
        throw new Error(t('files.schemaFailed'));
      }
      const stored = await deviceApi.getStoredFile(targetId, SETTINGS_PATH);
      const raw = stored?.content || '';
      const merged = mergeConfig(schema, raw);
      const graph = ensureGraph(merged as Record<string, unknown>);
      const devices = readDevices(graph);
      if (!stacklightStripsReady(device, devices) || !stacklightChannelsReady(device, devices)) {
        setError(t(stacklightUsesWs2812(device) ? 'deviceConfig.needStrips' : 'deviceConfig.needChannels'));
        return;
      }
      if (devices.length + 1 > MAX_DEVICES) {
        setError(t('flow.deviceLimit', { max: MAX_DEVICES }));
        return;
      }
      if (devices.some((existing) => existing.id === device.id)
        || (type === 'stacklight_legacy' && devices.some((existing) => (
          existing.id === 'stacklight' || existing.id === 'inputs' || existing.type === 'stacklight'
        )))) {
        setError(t('deviceConfig.duplicateId', { id: device.id }));
        return;
      }
      if (type === 'stacklight_legacy' && devices.length + 4 > MAX_DEVICES) {
        setError(t('flow.deviceLimit', { max: MAX_DEVICES }));
        return;
      }
      const zigbeeOn = Boolean((graph as { zigbee?: { enable?: boolean } }).zigbee?.enable);
      const saved = flowMode === 'none' ? null : await persistFlow(device, zigbeeOn);
      const wizard = catalogWizardFor(device.type);
      const next = wizard
        ? ensureGraph(wizard.apply(graph as Record<string, unknown>, device) as Record<string, unknown>)
        : ensureGraph({
          ...graph,
          devices: [...devices, device],
          routes: mergeRoutes(readRoutes(graph), saved?.doc?.routes || []),
        } as Record<string, unknown>);
      const json = generateConfigJson(schema, next as ConfigValues, raw);
      await deviceApi.saveStoredFile(targetId, SETTINGS_PATH, json);
      rememberControllerId(targetId);
      onPersisted?.(targetId, device);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || t('deviceConfig.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const canNextType = Boolean(type);
  const settingsDevice = { ...values, id: id.trim(), type } as GraphDevice;
  const gpioReady = peripheryGpiosReady(type, values);
  const canNextSettings = Boolean(
    type
    && id.trim()
    && (mode === 'local' || targetId)
    && !loading
    && gpioReady
    && stacklightStripsReady(settingsDevice, idSource)
    && stacklightChannelsReady(settingsDevice, idSource),
  );
  const canNextFlow = flowMode === 'none'
    || (flowMode === 'new' ? Boolean(slugCatalogName(newFlowName || suggestedName)) : Boolean(existingFlow));
  const lastStep = flowMode === 'none' ? 2 : 3;
  const activeStep = Math.min(step, lastStep);
  const steps = [
    t('deviceConfig.wizard.stepDevice'),
    t('deviceConfig.wizard.stepSettings'),
    t('deviceConfig.wizard.stepFlow'),
    ...(flowMode === 'none' ? [] : [t('deviceConfig.wizard.stepWidget')]),
  ];
  const canNext = activeStep === 0 ? canNextType : (activeStep === 1 ? canNextSettings : canNextFlow);

  const pickType = (nextType: string) => {
    setType(nextType);
    setError(null);
    setFlowNameTouched(false);
    setStep(1);
  };

  const setFlowModeSafe = (next: 'none' | 'new' | 'existing') => {
    setFlowMode(next);
    if (next === 'none') {
      setWidget('none');
      setStep((n) => Math.min(n, 2));
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>{t('deviceConfig.wizard.title')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
        {error && <Alert severity="error">{error}</Alert>}
        {loading ? (
          <CircularProgress size={24} sx={{ mx: 'auto', my: 2 }} />
        ) : activeStep === 0 ? (
          <>
            <Typography variant="body2" color="text.secondary">{t('deviceConfig.wizard.pickType')}</Typography>
            <Grid container spacing={2}>
              {peripheryTypes.map((item) => {
                const selected = type === item;
                const title = t(`flow.types.${item}`);
                const hintKey = `deviceConfig.wizard.tiles.${item}`;
                const hint = t(hintKey);
                const showHint = hint !== hintKey && hint !== title;
                return (
                  <Grid item xs={6} sm={6} md={4} key={item}>
                    <ButtonBase
                      onClick={() => pickType(item)}
                      sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: 2,
                        border: 2,
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'action.selected' : 'background.paper',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        textAlign: 'center',
                        minHeight: 168,
                        transition: 'border-color 120ms, background-color 120ms',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: selected ? 'action.selected' : 'action.hover',
                        },
                      }}
                    >
                      <Box sx={{ color: selected ? 'primary.main' : 'text.secondary', display: 'flex' }}>
                        <TypeIcon type={item} />
                      </Box>
                      <Typography variant="subtitle1" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
                        {title}
                      </Typography>
                      {showHint && (
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.25 }}>
                          {hint}
                        </Typography>
                      )}
                    </ButtonBase>
                  </Grid>
                );
              })}
            </Grid>
          </>
        ) : activeStep === 1 ? (
          <>
            {mode === 'persist' && (
              <Autocomplete
                options={fleet}
                value={selectedController}
                onChange={(_, next) => setSelectedController(next)}
                filterOptions={(options, state) => filterFleet(options, state.inputValue)}
                getOptionLabel={(option) => fleetLabel(option)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                groupBy={(option) => t('deviceConfig.familyGroup', {
                  family: controllerFamily(option.hostname),
                  status: option.online ? t('common.online') : t('common.offline'),
                })}
                noOptionsText={t('deviceConfig.noControllerHits')}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <div style={{ padding: '6px 16px', fontSize: 12, opacity: 0.7 }}>{params.group}</div>
                    <ul style={{ padding: 0 }}>{params.children}</ul>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoFocus
                    label={t('deviceConfig.controller')}
                    placeholder={t('deviceConfig.controllerSearch')}
                    helperText={t('deviceConfig.controllerSearchHint')}
                  />
                )}
              />
            )}
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{ color: 'primary.main', display: 'flex' }}><TypeIcon type={type} size={40} /></Box>
              <Typography variant="h6">{t(`flow.types.${type}`)}</Typography>
            </Box>
            <TextField
              label={t('deviceConfig.id')}
              value={id}
              onChange={(e) => {
                setId(e.target.value);
                setValues((prev) => ({ ...prev, id: e.target.value }));
              }}
            />
            {spec && Object.keys(formFields).length > 0 && (
              <SchemaObjectForm
                groupId="instance"
                fields={formFields}
                values={values}
                disabled={busy}
                onChange={(next) => setValues({ ...next, id, type })}
              />
            )}
            {!gpioReady && (type === 'gpio_input' || type === 'gpio_relay' || type === 'gpio_led' || type === 'gpio_buzzer') && (
              <Alert severity="warning">{t('deviceConfig.wizard.needGpios')}</Alert>
            )}
            {type === 'stacklight_legacy' && (
              <Alert severity="info">{t('deviceConfig.wizard.legacyHint')}</Alert>
            )}
            {type === 'stacklight' && stacklightUsesWs2812(values as GraphDevice) && (
              <StripRefsField
                device={settingsDevice}
                existing={idSource}
                disabled={busy}
                onChange={(strips) => setValues((prev) => ({ ...prev, strips }))}
              />
            )}
            {type === 'stacklight' && !stacklightUsesWs2812(values as GraphDevice) && (
              <ChannelRefsField
                device={settingsDevice}
                existing={idSource}
                disabled={busy}
                onChange={(channels) => setValues((prev) => ({ ...prev, channels }))}
              />
            )}
          </>
        ) : activeStep === 2 ? (
          <>
            <FormControl>
              <RadioGroup
                value={flowMode}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'none' || value === 'existing' || value === 'new') {
                    setFlowModeSafe(value);
                  }
                }}
              >
                <FormControlLabel value="none" control={<Radio />} label={t('deviceConfig.wizard.flowNone')} />
                <FormControlLabel value="new" control={<Radio />} label={t('deviceConfig.wizard.flowNew')} />
                <FormControlLabel
                  value="existing"
                  control={<Radio />}
                  label={t('deviceConfig.wizard.flowExisting')}
                  disabled={flows.length === 0}
                />
              </RadioGroup>
            </FormControl>
            {flowMode === 'new' && (
              <TextField
                label={t('deviceConfig.wizard.flowName')}
                value={newFlowName}
                onChange={(e) => {
                  setFlowNameTouched(true);
                  setNewFlowName(e.target.value);
                }}
                helperText={`${t('deviceConfig.wizard.flowNameHint')}: ${suggestedFlowLabel(hostname, id.trim() || type)}`}
              />
            )}
            {flowMode === 'existing' && (
              <Autocomplete
                options={flows.map((item) => item.name)}
                value={existingFlow || null}
                onChange={(_, next) => setExistingFlow(next || '')}
                renderInput={(params) => (
                  <TextField {...params} label={t('deviceConfig.wizard.flowPick')} />
                )}
              />
            )}
            {flowMode === 'none' && (
              <Alert severity="info">{t('deviceConfig.wizard.flowNoneHint')}</Alert>
            )}
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">{t('deviceConfig.wizard.widgetHint')}</Typography>
            {type === 'stacklight_legacy' && (
              <Alert severity="info">{t('deviceConfig.wizard.legacySimpleFlowHint')}</Alert>
            )}
            <FormControl>
              <RadioGroup
                value={widget}
                onChange={(e) => setWidget((e.target.value || 'none') as PeripheryWidgetType)}
              >
                {widgetChoices.map((choice) => (
                  <FormControlLabel
                    key={choice}
                    value={choice}
                    control={<Radio />}
                    label={widgetLabel(choice, t)}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={() => setStep((n) => Math.max(0, n - 1))} disabled={busy}>{t('deviceConfig.wizard.back')}</Button>
        )}
        {activeStep > 0 && activeStep < lastStep ? (
          <Button
            variant="contained"
            onClick={() => setStep((n) => n + 1)}
            disabled={busy || loading || !canNext}
          >
            {t('deviceConfig.wizard.next')}
          </Button>
        ) : activeStep === lastStep ? (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={busy || loading || !canNextSettings || !canNextFlow}
          >
            {t('deviceConfig.wizard.finish')}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default PeripheryDialog;
